import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { categories, videoSites } from '@/mocks/videoData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const hotSites = videoSites.slice(0, 8);
const newSites = [...videoSites].sort(() => Math.random() - 0.5).slice(0, 8);

function RatingStars({ rating }: { rating: number }) {
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <i
          key={i}
          className={`text-xs ${
            i < full ? 'ri-star-fill text-secondary-500' : i === full && half ? 'ri-star-half-fill text-secondary-500' : 'ri-star-fill text-background-300'
          }`}
        ></i>
      ))}
    </div>
  );
}

function SiteCard({ site }: { site: typeof videoSites[0]; index: number }) {
  return (
    <Link
      to={`/article/${site.slug}`}
      className="group block bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 hover:border-primary-300 hover:bg-background-50 transition-all duration-200"
    >
      <div className="flex items-start gap-3 md:gap-4">
        <div className="w-11 h-11 md:w-12 md:h-12 flex-shrink-0 rounded-lg bg-gradient-to-br from-primary-100 to-accent-100 flex items-center justify-center overflow-hidden">
          <img
            src={site.imageUrl}
            alt={site.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors truncate">{site.name}</h3>
            <span className="text-xs text-foreground-400 whitespace-nowrap">{site.visits}</span>
          </div>
          <p className="text-xs md:text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-2">{site.description}</p>
          <div className="flex items-center gap-3 flex-wrap">
            <RatingStars rating={site.rating} />
            <span className="text-xs text-primary-600 font-medium">{site.rating}</span>
            <div className="flex gap-1 flex-wrap">
              {site.tags.slice(0, 2).map((tag) => (
                <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-700">{tag}</span>
              ))}
            </div>
          </div>
        </div>
        <div className="flex-shrink-0 flex items-center">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 group-hover:bg-primary-100 transition-colors">
            <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-primary-600 transition-colors"></i>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  const [typedText, setTypedText] = useState('');
  const placeholders = ['电影资源', '热门电视剧', '最新综艺', '精彩动漫', '纪录片', '体育赛事'];
  const [phIndex, setPhIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const current = placeholders[phIndex];
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (charIndex < current.length) {
          setTypedText(current.slice(0, charIndex + 1));
          setCharIndex(charIndex + 1);
        } else {
          setTimeout(() => setIsDeleting(true), 1500);
        }
      } else {
        if (charIndex > 0) {
          setTypedText(current.slice(0, charIndex - 1));
          setCharIndex(charIndex - 1);
        } else {
          setIsDeleting(false);
          setPhIndex((phIndex + 1) % placeholders.length);
        }
      }
    }, isDeleting ? 50 : 100);

    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, phIndex]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      window.REACT_APP_NAVIGATE(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '福利视频导航',
            url: 'https://fulishipin.com',
            description: '精选优质视频资源聚合平台，提供专业分类导航服务，帮助用户快速找到所需视频内容。',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://fulishipin.com/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '福利视频导航',
            url: 'https://fulishipin.com',
            description: '精选优质视频资源聚合平台',
            contactPoint: {
              '@type': 'ContactPoint',
              email: 'contact@fulishipin.com',
              contactType: 'customer service',
            },
          }),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full overflow-hidden bg-gradient-to-br from-background-50 via-primary-50/30 to-accent-50/30">
            <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24 lg:py-32 text-center">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 mb-4">
                发现精彩
                <span className="text-primary-500">视频世界</span>
              </h1>
              <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto mb-8 leading-relaxed">
                福利视频导航为您汇聚全网精选优质视频资源，覆盖电影、电视剧、综艺、动漫等多元品类，助您快速找到心仪内容
              </p>

              <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-8">
                <div className="relative rounded-full shadow-sm">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={`搜索${typedText}... `}
                    className="w-full h-14 md:h-16 pl-14 pr-28 text-base rounded-full border-2 border-background-200 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-4 focus:ring-primary-100/50 transition-all"
                  />
                  <div className="absolute left-5 top-1/2 -translate-y-1/2 text-foreground-400">
                    <i className="ri-search-line text-xl"></i>
                  </div>
                  <button
                    type="submit"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-10 md:h-12 px-6 md:px-8 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm md:text-base font-medium transition-all whitespace-nowrap cursor-pointer"
                  >
                    搜 索
                  </button>
                </div>
              </form>

              <div className="flex items-center justify-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-400">热门搜索：</span>
                {['电影', '电视剧', '动漫', '综艺', 'NBA', '纪录片'].map((term) => (
                  <Link
                    key={term}
                    to={`/search?q=${term}`}
                    className="text-xs px-3 py-1 rounded-full bg-background-100 border border-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 hover:border-primary-200 transition-colors whitespace-nowrap"
                  >
                    {term}
                  </Link>
                ))}
              </div>
            </div>

            <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-primary-200/20 blur-3xl pointer-events-none"></div>
            <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full bg-accent-200/20 blur-3xl pointer-events-none"></div>
          </section>

          {/* Category Navigation */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">分类导航</h2>
                <p className="text-sm text-foreground-500 mt-1">按分类浏览您感兴趣的视频资源</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex items-center gap-3 p-4 md:p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-background-50 transition-all duration-200"
                >
                  <div className="w-10 h-10 md:w-11 md:h-11 flex-shrink-0 flex items-center justify-center rounded-lg bg-gradient-to-br from-primary-100 to-secondary-100 group-hover:from-primary-200 group-hover:to-secondary-200 transition-all">
                    <i className={`${cat.icon} text-lg md:text-xl text-primary-600`}></i>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">{cat.name}</h3>
                    <p className="text-xs text-foreground-400">{cat.count}个站点</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Hot Recommended */}
          <section className="bg-background-100/60 py-12 md:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">热门推荐</h2>
                  <p className="text-sm text-foreground-500 mt-1">编辑精选的高品质视频平台</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                {hotSites.map((site, idx) => (
                  <SiteCard key={site.slug} site={site} index={idx} />
                ))}
              </div>
            </div>
          </section>

          {/* Latest Added */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">最新收录</h2>
                <p className="text-sm text-foreground-500 mt-1">持续更新的优质视频站点</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {newSites.map((site, idx) => (
                <SiteCard key={site.slug} site={site} index={idx} />
              ))}
            </div>
          </section>

          {/* Stats */}
          <section className="bg-background-900 py-10 md:py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { label: '收录站点', value: '650+', icon: 'ri-global-line' },
                  { label: '覆盖分类', value: '8大类', icon: 'ri-stack-line' },
                  { label: '日活用户', value: '50万+', icon: 'ri-user-smile-line' },
                  { label: '好评率', value: '98.5%', icon: 'ri-thumb-up-line' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-background-700/50">
                      <i className={`${stat.icon} text-xl text-primary-400`}></i>
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-background-50">{stat.value}</div>
                    <div className="text-sm text-background-400 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}